import { useParams, Link } from 'react-router-dom'
import {
  Tags, Link2, Unlink, Loader2, Sparkles, Upload, RefreshCw,
  AlertCircle, ExternalLink, Plus,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import {
  useTagsetsForKB,
  useLinkTagsetToKB,
  useUnlinkTagsetFromKB,
  useReparseAllDocuments,
  type Tagset,
} from '@/hooks/useTagsets'

const statusBadge = (status: Tagset['status']) => {
  switch (status) {
    case 'DONE':
      return <Badge variant="default" className="bg-green-600">Ready</Badge>
    case 'RUNNING':
      return <Badge variant="secondary" className="animate-pulse">Processing</Badge>
    case 'FAIL':
      return <Badge variant="destructive">Failed</Badge>
    default:
      return <Badge variant="outline">Not Started</Badge>
  }
}

const sourceIcon = (source: Tagset['source_type']) => {
  switch (source) {
    case 'genclair':
      return <Sparkles className="h-4 w-4 text-purple-500" />
    case 'upload':
      return <Upload className="h-4 w-4 text-blue-500" />
    default:
      return <Tags className="h-4 w-4 text-muted-foreground" />
  }
}

export function KnowledgeBaseTags() {
  const { kbId } = useParams<{ kbId: string }>()
  const { toast } = useToast()

  const { data, isLoading } = useTagsetsForKB(kbId!)
  const linkTagset = useLinkTagsetToKB()
  const unlinkTagset = useUnlinkTagsetFromKB()
  const reparseAll = useReparseAllDocuments(kbId!)

  const handleLink = async (tagsetId: string, tagsetName: string) => {
    try {
      await linkTagset.mutateAsync({ tagsetId, kbId: kbId! })
      toast({ title: `Linked "${tagsetName}"`, description: 'Documents may need reparsing.' })
    } catch (err: any) {
      toast({ title: 'Link failed', description: err?.response?.data?.error || err.message, variant: 'destructive' })
    }
  }

  const handleUnlink = async (tagsetId: string, tagsetName: string) => {
    try {
      await unlinkTagset.mutateAsync({ tagsetId, kbId: kbId! })
      toast({ title: `Unlinked "${tagsetName}"` })
    } catch (err: any) {
      toast({ title: 'Unlink failed', description: err?.response?.data?.error || err.message, variant: 'destructive' })
    }
  }

  const handleReparse = async () => {
    try {
      await reparseAll.mutateAsync()
      toast({ title: 'Reparsing started', description: 'All documents are being reparsed with current tagsets.' })
    } catch (err: any) {
      toast({ title: 'Reparse failed', description: err?.response?.data?.error || err.message, variant: 'destructive' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const linked = data?.linked_tagsets || []
  const available = data?.available_tagsets || []
  const needsReparse = data?.kb_needs_reparse ?? false
  const reparseReason = data?.reparse_reason ?? ''

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Linked Tagsets</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Tagsets linked to this knowledge base enhance retrieval by adding structured metadata.
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/app/tagsets">
            <ExternalLink className="h-4 w-4 mr-2" />
            Manage All Tagsets
          </Link>
        </Button>
      </div>

      {/* Reparse Alert */}
      {needsReparse && (
        <Alert className="border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/30">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-sm text-amber-800 dark:text-amber-200">
              {reparseReason || 'Documents need reparsing to reflect tagset changes.'}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReparse}
              disabled={reparseAll.isPending}
              className="ml-4 shrink-0"
            >
              {reparseAll.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-1" />
              )}
              Reparse All
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Linked Tagsets */}
      {linked.length > 0 ? (
        <div className="space-y-3">
          {linked.map((tagset) => (
            <Card key={tagset.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {sourceIcon(tagset.source_type)}
                    <div>
                      <Link
                        to={`/app/tagsets/${tagset.id}`}
                        className="font-medium hover:underline"
                      >
                        {tagset.name}
                      </Link>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {tagset.tag_count} tags
                        </span>
                        {statusBadge(tagset.status)}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleUnlink(tagset.id, tagset.name)}
                    disabled={unlinkTagset.isPending}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Unlink className="h-4 w-4 mr-1" />
                    Unlink
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Tags className="h-10 w-10 text-muted-foreground mb-3" />
            <h3 className="font-semibold mb-1">No Tagsets Linked</h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Link a tagset below to enhance retrieval, or create a new one.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Available Tagsets to Link */}
      {available.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3">Available Tagsets</h3>
          <div className="space-y-2">
            {available.map((tagset) => (
              <Card key={tagset.id} className="border-dashed">
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {sourceIcon(tagset.source_type)}
                      <div>
                        <span className="text-sm font-medium">{tagset.name}</span>
                        <span className="text-xs text-muted-foreground ml-2">
                          {tagset.tag_count} tags
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLink(tagset.id, tagset.name)}
                      disabled={linkTagset.isPending || !tagset.is_ready}
                    >
                      <Link2 className="h-4 w-4 mr-1" />
                      Link
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Quick create link */}
      {available.length === 0 && linked.length === 0 && (
        <div className="text-center pt-4">
          <Button asChild>
            <Link to="/app/tagsets/create">
              <Plus className="h-4 w-4 mr-2" />
              Create a Tagset
            </Link>
          </Button>
        </div>
      )}
    </div>
  )
}
